import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { EditInventory } from "@src/components/editInventory/EditInventory";
import helperStyles from "@src/components/helpers.module.scss";
import { DateFormatsEnum, formatDate } from "@src/components/utils/dateUtils";
import { formatCurrency } from "@src/components/utils/numberUtils";
import { IColumn as IVColumn } from "@src/components/vGrid/VGrid";
import { ApStatus } from "@src/pages/home/children/pricing/components/inventory/components/ApStatus";
import { Marketplaces } from "@src/pages/home/children/pricing/components/inventory/components/Marketplaces";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import _ from "lodash";
import React, { lazy, Suspense } from "react";
import { AlertTriangle, Check, Lock, Minus } from "lucide-react";
import { MyTooltip } from "@src/components/tooltip/Tooltip";
import { settingsActions } from "..";

const lockedCell = (value: React.ReactNode, isLocked?: boolean | null, tooltip?: string, tooltipId?: string) => (
	<div
		className={`${helperStyles.secondaryText} ${helperStyles.textPrimary}`}
		style={{ display: "flex", alignItems: "center", gap: 6 }}>
		<span>{value}</span>
		{isLocked ? (
			<MyTooltip
				id={tooltipId ?? "lockTooltip"}
				popup={tooltip}
				popupStyle={{ fontSize: "1rem" }}
				element={<Lock size={12} />}
			/>
		) : null}
	</div>
);
const flagCell = (value?: boolean | null) =>
	value ? <Check size={14} color="#4E9D3A" /> : <Minus size={12} color="var(--textSecondary)" />;

const errorFlagCell = (value?: boolean | null) =>
	value ? <AlertTriangle size={14} color="#FF2D5F" /> : <Minus size={12} color="var(--textSecondary)" />;

const priceOrDash = (value?: number | null) => (value == null ? "-" : formatCurrency(value));

import { EventRecord } from "./queryEventsSlice";
import styles from "./settingsSlice.module.scss";
import Properties from "@src/pages/home/children/pricing/components/inventory/components/Properties";
import { EyeHide } from "@src/assets/general-icons/EyeHide";
import { EyeShow } from "@src/assets/general-icons/EyeShow";
import { ExternalNotes } from "@src/components/extenalNotes/ExternalNotes";
import { TagComponentModal } from "@src/components/tagComponent/TagComponent";
import { formatLastUpdate } from "@src/utils/general.utils";
import { getSplitsName } from "@src/components/utils/marketplaceUtils";
const InternalNotesLazy = lazy(() => import("@src/components/internalNotes/InternalNotes"));

interface ICustomizable {
	groupName: string;
	isChecked?: boolean;
}

export type ColumnListType = Array<[string, boolean]>; // [column, isChecked]
export type EventColumnType = IVColumn<EventRecord> & ICustomizable;
export const eventsColumns: Array<EventColumnType> = [
	{
		header: "Since Priced",
		id: "Days",
		accessor: "Days_Since_Last_Price_Update",
		hasTooltip: true,
		width: 120,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		formatValue: (x) => (x > 365 ? "Never" : x),
	},
	{
		header: "Category",
		id: "Category",
		accessor: "Cat_Desc",
		hasTooltip: true,
		width: 200,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Headliner",
		id: "Headliner",
		accessor: "Event_Headliner",
		hasTooltip: true,
		width: 250,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Event",
		id: "Event",
		accessor: "Event_Name",
		hasTooltip: true,
		width: 330,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Event ID",
		id: "EventID",
		accessor: "Event_ID",
		hasTooltip: true,
		width: 100,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Day",
		id: "Day",
		accessor: "Event_Date",
		width: 80,
		formatValue: (x) => formatDate(x, DateFormatsEnum.dddd),
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Date",
		id: "Date",
		accessor: "Event_Date",
		width: 85,
		formatValue: (x) => formatDate(x, DateFormatsEnum["MM/DD/YYYY"]),
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Time",
		id: "Time",
		accessor: "Event_Time",
		width: 80,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Venue",
		id: "Venue",
		accessor: "Venue_Name",
		hasTooltip: true,
		width: 180,
		isChecked: true,
		groupName: "Main Info",
	},
	{
		header: "City",
		id: "City",
		accessor: "Venue_City",
		hasTooltip: true,
		width: 180,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "State",
		id: "State",
		accessor: "Venue_State",
		hasTooltip: true,
		width: 180,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Open cost",
		id: "OpenCost",
		accessor: "Open_Cost",
		width: 90,
		formatValue: (x) => formatCurrency(x),
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Cost",
		id: "Cost",
		accessor: "Total_Cost",
		width: 90,
		formatValue: (x) => formatCurrency(x),
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Open",
		id: "Open",
		accessor: "Total_Open",
		width: 80,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},

	{
		header: "Reserved",
		id: "Reserved",
		accessor: "Total_Reserved",
		width: 60,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Sold",
		id: "Sold",
		accessor: "Total_Sold",
		width: 60,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Unpriced",
		id: "Unpriced",
		accessor: "Total_Unpriced",
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Unshared",
		id: "Unshared",
		accessor: "Total_Unshared",
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Tickets",
		accessor: "Total_Open",
		id: "Tickets",
		valueGetter: (params) => params.rowData.Total_Open + params.rowData.Total_Sold,
		formatValue: (x, { rowData }) => x + rowData.Total_Sold,
		width: 60,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Tag",
		id: "Tag",
		accessor: "Tags",
		formatValue: (_x, { rowData }) => {
			const originalTags = rowData?.Tags?.split("|") ?? [];
			const id = rowData.Event_ID;
			return (
				<TagComponentModal itemId={[id]} childIds={[id]} type="event" modalHeader="Event tags" originalTags={originalTags} />
			);
		},
		width: 50,
		isChecked: true,
		disableTitle: true,
		groupName: "Main Info",
	},
];
export const defaultEventColumns: ColumnListType = eventsColumns.map((x) => [`${x.id}`, !!x.isChecked]);

export const extractMarketplaceData = (data: EventInventoryRecord) => {
	const {
		StubHub_Listing_ID,
		StubHubInt_Listing_ID,
		TicketsNow_Listing_ID,
		Vivid_Listing_ID,
		TicketNetwork_Listing_ID,
		SeatGeek_Listing_ID,
		AXS_Listing_ID,
		Listing_ID,
		CIMS_Listing_ID,
	} = data;

	return {
		StubHub_Listing_ID,
		StubHubInt_Listing_ID,
		TicketsNow_Listing_ID,
		Vivid_Listing_ID,
		TicketNetwork_Listing_ID,
		SeatGeek_Listing_ID,
		AXS_Listing_ID,
		Listing_ID,
		CIMS_Listing_ID,
	};
};
type InventoryColumnType = IVColumn<EventInventoryRecord> & ICustomizable;

export const inventoryColumns: Array<InventoryColumnType> = [
	{
		header: "ListingID",
		id: "ListingID",
		accessor: "Listing_ID",
		width: 95,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Invoice ID",
		id: "InvoiceID",
		accessor: "Invoice_ID",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "PO ID",
		id: "PO_ID",
		accessor: "PO_ID",
		width: 95,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Company",
		id: "Company",
		accessor: "Company_Name",
		width: 150,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Section",
		id: "Section",
		accessor: "Section",
		getClassName: () => helperStyles.secondaryText,
		cellRenderer: ({ rowData }) =>
			lockedCell(
				rowData.Section,
				rowData.Section_Locked,
				"Section is locked from auto updating",
				`lock-section-${rowData.Item_ID}`,
			),
		width: 115,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Row",
		id: "Row",
		accessor: "Row",
		getClassName: () => helperStyles.secondaryText,
		cellRenderer: ({ rowData }) =>
			lockedCell(rowData.Row, rowData.Row_Locked, "Row is locked from auto updating", `lock-row-${rowData.Item_ID}`),
		width: 60,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Seat",
		id: "Seat",
		accessor: "Seats",
		valueGetter: ({ rowData }) => rowData.Seats || rowData.Seat_Number,
		formatValue: (x, { rowData }) => x || rowData.Seat_Number,
		getClassName: () => helperStyles.secondaryText,
		cellRenderer: ({ rowData }) =>
			lockedCell(
				rowData.Seats || rowData.Seat_Number,
				rowData.Seat_Locked,
				"Seats are locked from auto updating",
				`lock-seat-${rowData.Item_ID}`,
			),
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Quantity",
		id: "Quantity",
		accessor: "Quantity",
		valueGetter: ({ rowData }) => rowData.Quantity || 1,
		formatValue: (x) => x || 1,
		getClassName: () => helperStyles.secondaryText,
		width: 80,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Splits",
		id: "Splits",
		accessor: "StubHub_Splits",
		valueGetter: ({ rowData }) => {
			const splitName = getSplitsName(rowData.StubHub_Splits);
			return splitName;
		},
		formatValue: (x) => {
			if (typeof x === "string") return x;
			const val = Number(x);
			switch (val) {
				case 2:
					return "Pairs";
				case 1:
					return "Any";
				case 0:
					return "None";
				case -1:
				default:
					return "Default";
			}
		},
		getClassName: () => helperStyles.secondaryText,
		width: 68,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Cost",
		id: "Cost",
		accessor: "Item_Cost",
		valueGetter: ({ rowData }) => (rowData.Quantity ? rowData.Item_Cost / rowData.Quantity : 0),
		formatValue: (x, { rowData }) =>
			lockedCell(formatCurrency(x), rowData.Cost_Locked, "Cost is locked from auto updating", `lock-cost-${rowData.Item_ID}`),
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		formatTooltip: (x) => {
			return formatCurrency(x);
		},
		isEditable: (params) => !params.rowData.Invoice_ID,
		isCurrency: true,
		editableLabel: "Edit Cost",
		setTofixed: 2,
	},
	{
		header: "Price",
		id: "Price",
		accessor: "Item_Price",
		formatValue: (x, { rowData }) =>
			lockedCell(
				<div className={styles.priceContainer}>{formatCurrency(x)}</div>,
				rowData.Cost_Locked,
				"Price is locked (cost is locked)",
				`lock-price-${rowData.Item_ID}`,
			),
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		formatTooltip: (x) => {
			return formatCurrency(x);
		},
		isEditable: (params) => {
			return !params.rowData.Invoice_ID;
		},
		isCurrency: true,
		editableLabel: "Edit Price",
		setTofixed: 2,
	},
	{
		header: "GTFS",
		id: "GTFS",
		accessor: "FlashSaleDiscount",
		formatValue: (x) => `${x ?? 0}%`,
		width: 65,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "In-Hand",
		id: "InHand",
		accessor: "InHandDate",
		formatValue: (x, { rowData }) => (rowData.InHand ? "Now" : formatDate(x)),
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Invoice Date",
		id: "Invoice_Date",
		accessor: "Invoice_Date",
		width: 150,
		formatValue: (x) => formatDate(x, DateFormatsEnum["MM/DD/YYYY hh:mm A"], ""),
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Auto Pricer",
		id: "APStatus",
		accessor: "Is_AutoPriced",
		formatValue: (_x, { rowData }) => <ApStatus row={rowData} />,
		width: 106,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "AP",
		id: "AP",
		accessor: "Is_AutoPriced",
		headerTooltip: "Autopricer rule applied to this listing",
		formatValue: (x) => flagCell(x),
		formatTooltip: (x) => (x ? "Autopricer rule applied" : "No autopricer rule"),
		getClassName: () => styles.propertiesContainer,
		width: 60,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Floor",
		id: "FloorPrice",
		accessor: "Floor_Price",
		headerTooltip: "Autopricer floor price",
		formatValue: (x) => priceOrDash(x),
		formatTooltip: (x) => priceOrDash(x),
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "AF",
		id: "AF",
		accessor: "Is_At_Floor",
		headerTooltip: "Listing is priced at its autopricer floor",
		formatValue: (x) => flagCell(x),
		formatTooltip: (x) => (x ? "At floor" : "Not at floor"),
		getClassName: () => styles.propertiesContainer,
		width: 60,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Ceiling",
		id: "CeilingPrice",
		accessor: "Ceiling_Price",
		headerTooltip: "Autopricer ceiling price",
		formatValue: (x) => priceOrDash(x),
		formatTooltip: (x) => priceOrDash(x),
		width: 90,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "AC",
		id: "AC",
		accessor: "Is_At_Ceiling",
		headerTooltip: "Listing is priced at its autopricer ceiling",
		formatValue: (x) => flagCell(x),
		formatTooltip: (x) => (x ? "At ceiling" : "Not at ceiling"),
		getClassName: () => styles.propertiesContainer,
		width: 60,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "NC",
		id: "NC",
		accessor: "Is_No_Comparables",
		headerTooltip: "Autopricer found no comparable listings",
		formatValue: (x) => flagCell(x),
		formatTooltip: (x) => (x ? "No comparables" : "Comparables found"),
		getClassName: () => styles.propertiesContainer,
		width: 60,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Error",
		id: "APError",
		accessor: "Is_Error",
		headerTooltip: "Autopricer error on this listing",
		formatValue: (x) => errorFlagCell(x),
		formatTooltip: (x) => (x ? "Autopricer error" : "No autopricer error"),
		getClassName: () => styles.propertiesContainer,
		width: 70,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Allow Increase",
		id: "AllowIncrease",
		accessor: "Allow_Price_Increase",
		headerTooltip: "Autopricer is allowed to raise the price",
		formatValue: (x) => flagCell(x),
		formatTooltip: (x) => (x ? "Price increases allowed" : "Price increases not allowed"),
		getClassName: () => styles.propertiesContainer,
		width: 120,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "AP User",
		id: "APUser",
		accessor: "AutoPricer_User",
		headerTooltip: "User who applied the autopricer rule",
		formatValue: (x) => x || "-",
		hasTooltip: true,
		getClassName: () => helperStyles.secondaryText,
		width: 160,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "AP Cycle",
		id: "APCycle",
		accessor: "Minutes_Per_Cycle",
		headerTooltip: "Minutes between autopricer runs",
		formatValue: (x) => (x == null ? "-" : `${x} min`),
		getClassName: () => helperStyles.secondaryText,
		width: 100,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "AP Rule",
		id: "APRule",
		accessor: "MasterRuleName",
		headerTooltip: "Autopricer rule applied to this listing",
		formatValue: (x) => x || "-",
		hasTooltip: true,
		getClassName: () => helperStyles.secondaryText,
		width: 180,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Marketplaces",
		id: "Marketplaces",
		accessor: "Invoice_ID",
		formatValue: (x, { rowData }) => (x ? "" : <Marketplaces rowData={rowData} />),
		formatTooltip: (_x) => "Marketplaces",
		width: 190,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		isEditable: false,
		editableLabel: "Edit Sharing",
		onEditClick: (rowData) => {
			console.info("Edit sharing", rowData);
		},
	},
	{
		header: "Last Updated",
		id: "LastUpdated",
		accessor: "Last_Price_Update",
		getClassName: () => helperStyles.secondaryText,
		width: 300,
		hasTooltip: true,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		formatValue: (x) => formatLastUpdate(x),
	},
	{
		header: "Properties",
		id: "Properties",
		accessor: "Is_Error",
		formatTooltip: () => {
			return "";
		},
		formatValue: (_x, { rowData }) => <Properties rowData={rowData} />,
		getClassName: () => styles.propertiesContainer,
		width: 90,
		isChecked: true,
		groupName: "Main Info",
	},
	{
		header: "Hide Seats",
		id: "HideSeats",
		accessor: "Hide_Seats",
		formatValue: (x) => (x ? <EyeHide size="12" /> : <EyeShow size="12" />),
		formatTooltip: (x) => (x ? "Seats are hidden" : "Seats are visible"),
		getClassName: () => styles.propertiesContainer,
		width: 90,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Internal Notes",
		id: "InternalNotes",
		accessor: "Internal_Notes",
		hasTooltip: true,
		getClassName: () => helperStyles.secondaryText,
		width: 200,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		formatValue: (value, { rowData }) => (
			<Suspense fallback={<span>Loading...</span>}>
				<InternalNotesLazy
					tablePreview={true}
					refreshInventoryOnSave
					invoiceId={rowData.Invoice_ID?.toString()}
					listingId={rowData.Listing_ID?.toString()}
					value={value}
				/>
			</Suspense>
		),
	},
	{
		header: "External Notes",
		id: "ExternalNotes",
		accessor: "External_Notes",
		hasTooltip: true,
		getClassName: () => helperStyles.secondaryText,
		width: 200,
		isChecked: true,
		groupName: "Main Info",
		sortable: true,
		formatValue: (value, { rowData }) => (
			<ExternalNotes
				tablePreview={true}
				itemID={rowData.Item_ID}
				listingId={rowData.Listing_ID}
				notesText={rowData.External_Notes}
			/>
		),
	},
	{
		header: "Tag",
		id: "Tag",
		accessor: "Tags",
		formatValue: (_x, { rowData }) => {
			const originalTags = rowData?.Tags?.split("|") ?? [];
			const id = rowData.Listing_ID;
			const Child_Item_IDs = rowData.Child_Item_IDs;

			return (
				<TagComponentModal
					itemId={[id]}
					childIds={Child_Item_IDs}
					type="inventory"
					modalHeader="Inventory tags"
					originalTags={originalTags}
				/>
			);
		},
		formatTooltip: (_x) => "Tags",
		width: 50,
		isChecked: true,
		disableTitle: true,
		groupName: "Main Info",
	},
	{
		header: "TA Date",
		id: "TADate",
		accessor: "Tickets_Available_Date",
		formatValue: (x) => formatDate(x),
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Automation Notes",
		id: "AutomationNotes",
		accessor: "Automation_Notes",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Order Number",
		id: "OrderNumber",
		accessor: "Internal_Notes",
		valueGetter: ({ rowData }) => rowData.Internal_Notes?.split("/")[1]?.trim(),
		formatValue: (x) => x?.split("/")[1]?.trim(),
		width: 200,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Purchaser",
		id: "Purchaser",
		accessor: "Purchaser_Name",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Account",
		id: "Account",
		accessor: "Account_Name",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Vendor",
		id: "Vendor",
		accessor: "Vendor",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Credit card",
		id: "CreditCard",
		accessor: "Credit_Card",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "PO ID",
		id: "POID",
		accessor: "PO_ID",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "PO Date",
		id: "PODate",
		accessor: "PO_Date",
		width: 110,
		formatValue: (x) => formatDate(x),
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: "Invoice Amount",
		id: "InvoiceAmount",
		accessor: "Invoice_Amount",
		width: 110,
		groupName: "Main Info",
		sortable: true,
	},
	{
		header: " ",
		id: "Options",
		accessor: "Item_ID",
		formatValue: (_x) => <EditInventory itemId={_x} />,
		width: 60.65,
		isSticky: true,
		isChecked: true,
		groupName: "Main Info",
		formatTooltip: (_x) => "Edit",
	},
];
const checkForDuplicateIDs = (columns: Array<{ id: string }>) => {
	const ids = columns.map((column) => column.id);
	const duplicateIDs = _.filter(ids, (value, index, iteratee) => _.includes(iteratee, value, index + 1));
	if (duplicateIDs.length > 0) throw new Error(`Duplicate IDs found: ${duplicateIDs.join(", ")}`);
};
checkForDuplicateIDs(eventsColumns);
checkForDuplicateIDs(inventoryColumns);

export const defaultInventoryColumns: ColumnListType = inventoryColumns.map((x) => [`${x.id}`, !!x.isChecked]);

export const UNGROUPED_INVENTORY_COLUMN_IDS: Array<string> = [
	"ListingID",
	"ItemID",
	"PO_ID",
	"PODate",
	"Company",
	"Section",
	"Row",
	"Seat",
	"Price",
	"InHand",
	"AP",
	"FloorPrice",
	"AF",
	"CeilingPrice",
	"AC",
	"NC",
	"APError",
	"AllowIncrease",
	"APUser",
	"APCycle",
	"APRule",
	"Properties",
	"InternalNotes",
	"ExternalNotes",
	"Tag",
];

// Ungrouped-only column — not registered in inventoryColumns so it never appears
// in the grouped Inventory customize tab.
const itemIdUngroupedColumn: InventoryColumnType = {
	header: "Item ID",
	id: "ItemID",
	accessor: "Item_ID",
	width: 95,
	groupName: "Main Info",
	sortable: true,
};

const ungroupedSourceColumns: Array<InventoryColumnType> = [...inventoryColumns, itemIdUngroupedColumn];

export const ungroupedInventoryColumns: Array<InventoryColumnType> = UNGROUPED_INVENTORY_COLUMN_IDS.map((id) =>
	ungroupedSourceColumns.find((c) => c.id === id),
).filter((c): c is InventoryColumnType => !!c);

export const defaultUngroupedInventoryColumns: ColumnListType = ungroupedInventoryColumns.map((x) => [`${x.id}`, true]);

export interface IColumn {
	label: string;
	id: string;
	isSticky: boolean;
	checked: boolean;
}

export interface IGroup {
	header: string;
	columns: Array<IColumn>;
}

export interface IColumnGroups {
	Event: Array<IGroup>;
	Inventory: Array<IGroup>;
	UngroupedInventory: Array<IGroup>;
}

export const defaultGroups: IColumnGroups = {
	Event: _.map(
		_.groupBy(eventsColumns, (x) => x.groupName),
		(value, key) => {
			return {
				header: key,
				columns: value.map((x) => {
					return {
						label: `${x.header}`,
						id: `${x.id}`,
						isSticky: !!x.isSticky,
						checked: !!x.isChecked,
					};
				}),
			};
		},
	),
	Inventory: _.map(
		_.groupBy(inventoryColumns, (x) => x.groupName),
		(value, key) => {
			return {
				header: key,
				columns: value.map((x) => {
					return {
						label: `${x.header}`,
						id: `${x.id}`,
						isSticky: !!x.isSticky,
						checked: !!x.isChecked,
					};
				}),
			};
		},
	),
	UngroupedInventory: _.map(
		_.groupBy(ungroupedInventoryColumns, (x) => x.groupName),
		(value, key) => {
			return {
				header: key,
				columns: value.map((x) => {
					return {
						label: `${x.header}`,
						id: `${x.id}`,
						isSticky: !!x.isSticky,
						checked: true,
					};
				}),
			};
		},
	),
};

interface ISelectedEvent {
	columns: {
		Event: Array<EventColumnType>;
		Inventory: Array<InventoryColumnType>;
		UngroupedInventory: Array<InventoryColumnType>;
	};
}

const initialState: ISelectedEvent = {
	columns: {
		Event: [],
		Inventory: [],
		UngroupedInventory: [],
	},
};

export const setEventColumns = createAsyncThunk<unknown, ColumnListType>(
	"settings/setEventColumns",
	async (columns, { dispatch }) => {
		dispatch(settingsSlice.actions.setEventColumns(columns));
		dispatch(settingsActions.setPricingEventsColumns(columns));
	},
);

export const setInventoryColumns = createAsyncThunk<unknown, { columns: ColumnListType; hasEditLockDownCosts: boolean }>(
	"settings/setInventoryColumns",
	async ({ columns, hasEditLockDownCosts }, { dispatch }) => {
		dispatch(settingsSlice.actions.setInventoryColumns({ columns, hasEditLockDownCosts }));
		dispatch(settingsActions.setPricingInventoryColumns(columns));
	},
);

export const setUngroupedInventoryColumns = createAsyncThunk<unknown, ColumnListType>(
	"settings/setUngroupedInventoryColumns",
	async (columns, { dispatch }) => {
		dispatch(settingsSlice.actions.setUngroupedInventoryColumns(columns));
		dispatch(settingsActions.setPricingUngroupedInventoryColumns(columns));
	},
);

export const settingsSlice = createSlice({
	name: "pricing.settingsSlice",
	initialState,
	reducers: {
		setEventColumns: (state, action: PayloadAction<ColumnListType>) => {
			const columns = new Array<EventColumnType>();

			action.payload.forEach((x) => {
				if (!x[1]) return;
				const column = eventsColumns.find((c) => c.id === x[0]);
				if (column) columns.push(column);
			});

			return {
				...state,
				columns: {
					...state.columns,
					Event: columns,
				},
			};
		},
		setInventoryColumns: (state, action: PayloadAction<{ columns: ColumnListType; hasEditLockDownCosts: boolean }>) => {
			const { columns, hasEditLockDownCosts } = action.payload;
			const result = new Array<InventoryColumnType>();

			columns.forEach((x) => {
				if (!x[1]) return;
				const column = inventoryColumns.find((c) => c.id === x[0]);
				if (!column) return;

				result.push(
					column.id === "Cost"
						? {
								...column,
								isEditable: (params: { rowData: EventInventoryRecord }) =>
									!params.rowData.Invoice_ID || hasEditLockDownCosts,
							}
						: column,
				);
			});

			return {
				...state,
				columns: { ...state.columns, Inventory: result },
			};
		},
		setUngroupedInventoryColumns: (state, action: PayloadAction<ColumnListType>) => {
			const result = new Array<InventoryColumnType>();

			action.payload.forEach((x) => {
				if (!x[1]) return;
				const column = ungroupedInventoryColumns.find((c) => c.id === x[0]);
				if (column) result.push(column);
			});

			return {
				...state,
				columns: { ...state.columns, UngroupedInventory: result },
			};
		},
	},
});
